import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { EmptySecurityConstraints, SecurityConstraintsEvaluator } from '@twist/core';
import { TwSearchInputComponent } from '@twist/ui-form';
import {
  TwModalBaseComponent,
  TwModalBodyComponent,
  TwModalHeaderComponent,
} from '@twist/ui-overlays';
import { NavPageIndexEntry, NavRegistry } from '@twist/ui-navigation';
import { TwEmptyStateComponent } from '@twist/ui-primitives';

/**
 * ⌘K command palette (Phase 2). Modal content opened via
 * `TwModalService.open(NavCommandPaletteComponent)` from the topbar.
 *
 * Results come from `NavRegistry.pageIndex(query, 'palette')` — i.e. the
 * flat page index narrowed to nodes that opt into the `palette` surface
 * (surface-agnostic nodes included). Each result is then SECURITY-FILTERED
 * by pre-resolving the owning `descriptor(id).security` through the SAME
 * `SecurityConstraintsEvaluator` the sidebar `*authGuard` uses — no
 * separate access check. Allowed ids are collected into a signal; results
 * the user can't access never render. Selecting a result navigates and
 * closes the modal.
 */
@Component({
  selector: 'app-nav-command-palette',
  standalone: true,
  imports: [TwModalHeaderComponent, TwModalBodyComponent, TwSearchInputComponent, TwEmptyStateComponent],
  templateUrl: './nav-command-palette.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavCommandPaletteComponent extends TwModalBaseComponent<void> {
  private readonly registry = inject(NavRegistry);
  private readonly evaluator = inject(SecurityConstraintsEvaluator);
  private readonly _router = inject(Router);

  /** Live search query, bound two-way to the tw-search-input. */
  readonly query = signal('');

  /**
   * Ids the current user is allowed to navigate to. Populated
   * asynchronously by `resolveAccess()` (one evaluate() per unique
   * palette node) — entries absent from this set are hidden.
   */
  private readonly allowedIds = signal<ReadonlySet<string>>(new Set());

  /** All palette-surface page-index entries (query-independent base). */
  private readonly paletteEntries = this.registry.pageIndex(undefined, 'palette');

  /**
   * Query-filtered, security-filtered results rendered in the list.
   * pageIndex does the label/path match; allowedIds gates visibility.
   */
  readonly results = computed<NavPageIndexEntry[]>(() => {
    const allowed = this.allowedIds();
    return this.registry
      .pageIndex(this.query(), 'palette')
      .filter((e) => allowed.has(e.id));
  });

  constructor() {
    super();
    void this.resolveAccess();
  }

  /**
   * Pre-resolves access for every palette node once, using the shared
   * evaluator. A descriptor with no `security` is treated as
   * authenticated-only (EmptySecurityConstraints) — identical to the
   * sidebar `*authGuard` semantics.
   */
  private async resolveAccess(): Promise<void> {
    const allowed = new Set<string>();
    await Promise.all(
      this.paletteEntries.map(async (entry) => {
        const security = this.registry.descriptor(entry.id)?.security;
        const merged = { ...EmptySecurityConstraints, ...security };
        if (await this.evaluator.evaluate(merged)) {
          allowed.add(entry.id);
        }
      }),
    );
    this.allowedIds.set(allowed);
  }

  protected onSelect(entry: NavPageIndexEntry): void {
    void this._router.navigateByUrl(entry.path);
    this.closeModal();
  }
}
